/**
 * Turnkey Public Configuration
 * Single source of truth for Turnkey config with runtime override support.
 * 
 * Priority order:
 * 1. window.__TURNKEY_PUBLIC_CONFIG__ (runtime override for emergency hotfix)
 * 2. import.meta.env.VITE_* environment variables (build-time)
 * 
 * Rebuilt: 2024-12-20 to pick up env vars
 */

declare global {
  interface Window {
    __TURNKEY_PUBLIC_CONFIG__?: {
      organizationId?: string;
      authProxyConfigId?: string;
      apiBaseUrl?: string;
    };
  }
}

export interface TurnkeyPublicConfig {
  organizationId: string | null;
  authProxyConfigId: string | null;
  apiBaseUrl: string | null;
  redirectUri: string;
}

const REQUIRED_KEYS = [
  'VITE_TURNKEY_ORGANIZATION_ID',
  'VITE_TURNKEY_AUTH_PROXY_CONFIG_ID',
] as const;

/**
 * Get the current origin safely (SSR-safe)
 */
function getOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://launch.dextoro.com';
}

/**
 * Compute OAuth redirect URI with fallback chain:
 * 1. VITE_TURNKEY_OAUTH_REDIRECT_URI env var (explicit override)
 * 2. Runtime computed from window.location.origin + /auth/turnkey
 */
function computeRedirectUri(): string {
  const envOverride = import.meta.env.VITE_TURNKEY_OAUTH_REDIRECT_URI;
  if (envOverride) {
    return envOverride;
  }
  return `${getOrigin()}/auth/turnkey`;
}

/**
 * Get Turnkey public configuration from all sources
 */
export function getTurnkeyPublicConfig(): TurnkeyPublicConfig {
  // Check for runtime override first (emergency hotfix capability)
  const runtimeConfig = typeof window !== 'undefined' ? window.__TURNKEY_PUBLIC_CONFIG__ : undefined;
  
  // Build config with priority: runtime override > env vars
  const organizationId = runtimeConfig?.organizationId 
    || import.meta.env.VITE_TURNKEY_ORGANIZATION_ID 
    || null;
  
  const authProxyConfigId = runtimeConfig?.authProxyConfigId 
    || import.meta.env.VITE_TURNKEY_AUTH_PROXY_CONFIG_ID 
    || null;
  
  const apiBaseUrl = runtimeConfig?.apiBaseUrl 
    || import.meta.env.VITE_TURNKEY_API_BASE_URL 
    || null;
  
  // Compute redirect URI with env override support
  const redirectUri = computeRedirectUri();
  
  return {
    organizationId,
    authProxyConfigId,
    apiBaseUrl,
    redirectUri,
  };
}

/**
 * Get list of missing required Turnkey config keys
 */
export function getTurnkeyMissingKeys(): string[] {
  const config = getTurnkeyPublicConfig();
  const missing: string[] = [];
  
  if (!config.organizationId) {
    missing.push('VITE_TURNKEY_ORGANIZATION_ID');
  }
  if (!config.authProxyConfigId) {
    missing.push('VITE_TURNKEY_AUTH_PROXY_CONFIG_ID');
  }
  
  return missing;
}

/**
 * Check if Turnkey is properly configured with all required values
 */
export function isTurnkeyConfigured(): boolean {
  const config = getTurnkeyPublicConfig();
  return !!(config.organizationId && config.authProxyConfigId);
}

/**
 * Get diagnostics info for debugging (safe to display, no full secrets)
 */
export function getTurnkeyDiagnostics() {
  const config = getTurnkeyPublicConfig();
  const missingKeys = getTurnkeyMissingKeys();
  
  return {
    origin: getOrigin(),
    redirectUri: config.redirectUri,
    hasOrganizationId: !!config.organizationId,
    hasAuthProxyConfigId: !!config.authProxyConfigId,
    hasApiBaseUrl: !!config.apiBaseUrl,
    isConfigured: isTurnkeyConfigured(),
    missingKeys,
    // Show last 4 chars of IDs for verification (safe for public config)
    organizationIdSuffix: config.organizationId ? `...${config.organizationId.slice(-4)}` : null,
    authProxyConfigIdSuffix: config.authProxyConfigId ? `...${config.authProxyConfigId.slice(-4)}` : null,
    hasRuntimeOverride: typeof window !== 'undefined' && !!window.__TURNKEY_PUBLIC_CONFIG__,
  };
}
